<script>
  import { rowClick } from '../lib/row-link.js';
  import Layout from '../components/Layout.svelte';
  import { companyInvoicesApi, scadenzeApi, fetchHealth } from '../stores/api.js';
  import { activeCompanyId, companiesLoading } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { push, link } from 'svelte-spa-router';
  import { t } from '../i18n/index.js';
  import { ArrowRight, AlertTriangle, CheckCircle, XCircle, FileText, Plus } from '../lib/icons/index.js';
  import StatusBadge from '../components/StatusBadge.svelte';
  import StatusLegend from '../components/StatusLegend.svelte';
  import DirectionBadge from '../components/DirectionBadge.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import { formatDateTime, formatEuro, isFailedInvoice, isAwaitingSdiOutcome } from '../lib/labels.js';

  let health = null;
  let outboundInvoices = [];
  let inboundInvoices = [];
  let recentInvoices = [];
  let scadenze = null;
  let loading = true;
  let error = null;

  // Deep links for the "N da controllare" counters. `year=all` and `formato=all`
  // are not decoration: the counters are computed over the whole list this page
  // loaded, while the Fatture page defaults to the current year and to FPR12, so
  // without them the destination silently drops invoices the counter promised.
  const FAILED_LINK = {
    outbound: '/invoices?direction=outbound&status=failed&year=all&formato=all',
    inbound: '/invoices?direction=inbound&status=failed&year=all',
  };

  // The scadenzario opened on exactly what each card counted, so the number is
  // a promise the destination keeps.
  const SCADENZE_LINK = {
    incassi: '/scadenzario?direction=incassi&status=scadute',
    pagamenti: '/scadenzario?direction=pagamenti&status=scadute',
  };

  // The four cards below all count rows. These two are the only figures on the
  // page denominated in money, and they are the ones an owner opens the app
  // for: what is late coming in, and what is late going out.
  $: incassi = scadenze?.totals?.incassi || null;
  $: pagamenti = scadenze?.totals?.pagamenti || null;
  // True when the server's safety cap stopped the walk: the money figures are
  // then partial. Said out loud when it happens — these figures are read as
  // facts, and a quietly partial one is worse than none.
  $: moneyPartial = scadenze?.totalsTruncated === true;

  $: outboundCount = outboundInvoices.length;
  $: inboundCount = inboundInvoices.length;
  // "In attesa di una ricevuta di consegna o di scarto" — that is a property of
  // the invoices this company sent, and it is readable by every member. The
  // cross-tenant /api/admin/transmissions/pending that used to feed this card
  // answers 403 to anyone but a global admin, so the counter silently read 0.
  //
  // isPendingStatus() is the wrong bucket here: it also covers 'created',
  // 'xml_generated' and 'queued_for_send', i.e. invoices still sitting in this
  // system, which would inflate a card whose tooltip promises "fatture inviate a
  // SdI in attesa di una ricevuta".
  $: pendingCount = outboundInvoices.filter(i => isAwaitingSdiOutcome(i.status)).length;
  // The failure bucket comes from lib/labels.js, which mirrors the InvoiceStatus
  // enum in src/domain/state-machine.js. The private list that used to live here
  // named three statuses no transition can produce and left out the six that
  // matter — a scartata invoice showed a red badge in the table below while the
  // "X da controllare" counter above it read 0. Judged on the whole invoice: an
  // expected mancata consegna (no codice destinatario, no PEC) is not a problem.
  $: failedOutbound = outboundInvoices.filter(isFailedInvoice).length;
  $: failedInbound = inboundInvoices.filter(isFailedInvoice).length;

  // The dashboard shows the company selected in the sidebar switcher, so it
  // waits for that id rather than reading every tenant's invoices through the
  // admin endpoint.
  $: companyId = $activeCompanyId;
  $: if (companyId) loadDashboard(companyId);
  // Still waiting on the company list means still loading — the "no invoices
  // yet" empty state would otherwise flash before the first company arrives.
  $: if (!companyId) { outboundInvoices = []; inboundInvoices = []; recentInvoices = []; scadenze = null; loading = $companiesLoading; }

  async function loadDashboard(cid) {
    loading = true;
    error = null;
    try {
      // The scadenzario totals are a nice-to-have on this page: a failure there
      // must not blank the invoice cards, so it degrades to no money row.
      //
      // `view: 'totali'` asks the server for the sums over the whole
      // scadenzario. Reading a page of instalments and adding it up here meant
      // downloading up to a thousand objects to print four numbers, and made
      // every figure silently stop at the thousandth instalment.
      const [all, h, due] = await Promise.all([
        companyInvoicesApi.list(cid, { limit: 200 }),
        fetchHealth().catch(() => null),
        scadenzeApi.list(cid, { status: 'aperte', view: 'totali' }).catch(() => null),
      ]);
      scadenze = due;

      const invoices = Array.isArray(all) ? all : [];
      outboundInvoices = invoices.filter(i => i.direction === 'outbound');
      inboundInvoices = invoices.filter(i => i.direction === 'inbound');
      health = h;

      // Most recent invoices for the table (already sorted DESC by API)
      recentInvoices = invoices.slice(0, 10);
    } catch (err) {
      error = err;
    } finally {
      loading = false;
    }
  }

  function goToInvoice(id) {
    push(`/invoices/${id}`);
  }
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('dashboard.title')}</h1>
      <p class="view-subtitle">{$t('dashboard.subtitle')}</p>
    </div>
    <!-- Invoices.svelte gates its own two CTAs the same way: a role that
         cannot write is not offered the form it would lose. -->
    {#if $canWriteInCompany}
      <div class="view-actions">
        <button class="btn btn-primary gap-2" on:click={() => push('/invoices/new')}>
          <Plus size={16} aria-hidden="true" /> {$t('nav.newInvoice')}
        </button>
      </div>
    {/if}
  </div>

  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} />
  {:else}
    <!-- Money first. Everything below this strip counts documents; these
         figures count euros, which is the question the app gets opened for.
         Each links into the scadenzario already filtered to what it showed. -->
    {#if incassi || pagamenti}
      <dl class="summary-strip" aria-label={$t('dashboard.moneyLabel')}>
        {#each [{ key: 'incassi', totals: incassi, label: 'daIncassare', tooltip: 'daIncassareTooltip' }, { key: 'pagamenti', totals: pagamenti, label: 'daPagare', tooltip: 'daPagareTooltip' }] as card}
          <div class="summary-item">
            <dt class="summary-label flex items-center gap-1">
              {$t(`dashboard.${card.label}`)}<InfoHint text={$t(`dashboard.${card.tooltip}`)} />
            </dt>
            <dd class="summary-value" class:text-danger={card.totals?.scadute.importo > 0}>
              {formatEuro(card.totals?.scadute.importo ?? 0)}
            </dd>
            <dd class="summary-note">{$t('dashboard.overdueLabel')}</dd>
            <dd>
              <a href={SCADENZE_LINK[card.key]} use:link class="btn-link">
                {$t('dashboard.openScadenzario')} <ArrowRight size={14} aria-hidden="true" />
              </a>
            </dd>
          </div>
        {/each}
        <div class="summary-item">
          <dt class="summary-label flex items-center gap-1">
            {$t('dashboard.dueSoonTitle')}<InfoHint text={$t('dashboard.dueSoonTooltip')} />
          </dt>
          <dd class="summary-value">{formatEuro(incassi?.inScadenza.importo ?? 0)}</dd>
          <dd class="summary-note">{$t('dashboard.dueSoonNote', { importo: formatEuro(pagamenti?.inScadenza.importo ?? 0) })}</dd>
        </div>
      </dl>
      {#if moneyPartial}
        <p class="text-xs text-warning -mt-3 mb-5 flex items-start gap-1" role="status">
          <AlertTriangle size={12} class="flex-shrink-0 mt-0.5" aria-hidden="true" />
          {$t('dashboard.partialTotals')}
        </p>
      {/if}
    {/if}

    <!-- The counters are a promise: following one must show those invoices and
         nothing else. They are computed over every year and both formati, so
         the links say so explicitly — the Fatture page otherwise opens on the
         current year and FPR12 and would hide part of what was counted. -->
    {#each [{ n: failedOutbound, key: 'Outbound', link: FAILED_LINK.outbound }, { n: failedInbound, key: 'Inbound', link: FAILED_LINK.inbound }] as notice}
      {#if notice.n > 0}
        <div class="attention-notice">
          <div class="notice-copy">
            <span class="notice-mark" aria-hidden="true">!</span>
            <p>
              <strong>{notice.n === 1 ? $t(`dashboard.attention${notice.key}One`) : $t(`dashboard.attention${notice.key}`, { n: notice.n })}</strong><br />
              {$t('dashboard.failedTooltip')}
            </p>
          </div>
          <a href={notice.link} use:link class="btn-link">
            {$t('dashboard.attentionAction')} <ArrowRight size={14} aria-hidden="true" />
          </a>
        </div>
      {/if}
    {/each}

    <dl class="summary-strip" aria-label={$t('dashboard.countsLabel')}>
      <div class="summary-item">
        <dt class="summary-label">{$t('dashboard.outbound')}</dt>
        <dd class="summary-value">{outboundCount}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label">{$t('dashboard.inbound')}</dt>
        <dd class="summary-value">{inboundCount}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label flex items-center gap-1">
          {$t('dashboard.pendingTx')}<InfoHint text={$t('dashboard.pendingTxTooltip')} />
        </dt>
        <dd class="summary-value">{pendingCount}</dd>
      </div>
      <div class="summary-item">
        <dt class="summary-label flex items-center gap-1">
          {$t('dashboard.service')}<InfoHint text={$t('dashboard.serviceTooltip')} />
        </dt>
        <!-- No environment line: GET /health answers { status, timestamp } and
             nothing else. The environment is admin-only (GET /api/admin/diagnostics/config). -->
        <dd class="summary-value flex items-center gap-2">
          {#if health}
            <CheckCircle size={18} class="text-success" aria-hidden="true" />
          {:else}
            <XCircle size={18} class="text-danger" aria-hidden="true" />
          {/if}
          {health ? $t('dashboard.statusOk') : $t('dashboard.statusDown')}
        </dd>
      </div>
    </dl>

    <section class="mt-10" aria-labelledby="recent-invoices-title">
      <div class="section-heading">
        <h2 id="recent-invoices-title">{$t('dashboard.recentInvoices')}</h2>
        <a href="/invoices" use:link class="btn-link">{$t('dashboard.allInvoices')} <ArrowRight size={14} aria-hidden="true" /></a>
      </div>
      {#if recentInvoices.length === 0}
        <EmptyState icon={FileText} title={$t('dashboard.emptyTitle')} body={$t('dashboard.emptyBody')}>
          {#if $canWriteInCompany}
            <button class="btn btn-primary btn-sm gap-1" on:click={() => push('/invoices/new')}>
              <Plus size={16} aria-hidden="true" /> {$t('dashboard.emptyCta')}
            </button>
          {/if}
        </EmptyState>
      {:else}
        <StatusLegend invoices={recentInvoices} />
        <div class="overflow-x-auto">
          <table class="table-modern table-stack table-cards cards-recent" role="table">
            <thead class="table-header" role="rowgroup">
              <tr role="row">
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('dashboard.col.direction')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('dashboard.col.invoiceNumber')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">
                  <span class="inline-flex items-center gap-1">{$t('dashboard.col.sdiId')}<InfoHint text={$t('dashboard.sdiIdTooltip')} /></span>
                </th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('dashboard.col.status')}</th>
                <th class="table-cell table-header-cell text-left" role="columnheader">{$t('dashboard.col.created')}</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {#each recentInvoices as inv (inv.id)}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                <tr class="table-row" role="row" on:click={(event) => rowClick(event, () => goToInvoice(inv.id))}>
                  <td class="table-cell cell-direction" role="cell" data-label={$t('dashboard.col.direction')}><DirectionBadge direction={inv.direction} /></td>
                  <td class="table-cell cell-number" role="cell" data-label={$t('dashboard.col.invoiceNumber')}>
                    <a href="/invoices/{inv.id}" use:link class="invoice-link"
                      aria-label={$t('invoices.openInvoice', { numero: inv.invoiceNumber || inv.progressivoInvio || '-' })}>
                      {inv.invoiceNumber || inv.progressivoInvio || '-'}
                    </a>
                  </td>
                  <td class="table-cell cell-meta mono text-xs" class:is-empty={!inv.identificativoSdi} role="cell" data-label={$t('dashboard.col.sdiId')}>{inv.identificativoSdi || '-'}</td>
                  <td class="table-cell cell-status" role="cell" data-label={$t('dashboard.col.status')}><StatusBadge status={inv.status} invoice={inv} /></td>
                  <td class="table-cell cell-date text-xs" role="cell" data-label={$t('dashboard.col.created')}>{formatDateTime(inv.createdAt)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
  {/if}
</Layout>
